<script setup lang="ts">
import { computed } from 'vue';

import type {
  ButtonAttrs,
  InteractiveElementAttrs,
} from '@/types/links-buttons';

const props = defineProps<{
  text: string;
  to?: string;
  href?: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  next?: boolean;
  type?: ButtonAttrs['type'];
}>();

const classList = computed(() => {
  return {
    'btn-secondary': props.variant === 'secondary',
    'btn-tertiary': props.variant === 'tertiary',
    'btn-next': props.next,
  };
});

const wrapperType = computed(() => {
  if (props.href) {
    return 'a';
  }
  if (props.to) {
    return 'router-link';
  }
  return 'button';
});

const linkAttrs = computed<InteractiveElementAttrs>(() => {
  if (props.href) {
    return {
      href: props.href,
      target: '_blank',
      rel: 'noopener noreferrer',
    };
  }

  if (props.to) {
    return { to: props.to };
  }

  return { type: props.type ? props.type : 'button' };
});
</script>

<template>
  <component
    :is="wrapperType"
    v-bind="linkAttrs"
    class="btn"
    :class="classList"
  >
    <span v-text="text" />
  </component>
</template>

<style lang="less">
.btn {
  border-radius: var(--base-border-radius);
  outline: none;
  padding: 12 24px;
  min-height: 50px;
  line-height: 50px;
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 500;
  background: #fff;
  border-color: #fff;
  color: var(--primary-900);
  display: block;
  text-align: center;
  text-decoration: none;
  width: 100%;

  &.btn-secondary {
    border: 1px solid var(--text-color-blue);
    background: transparent;
    color: var(--text-color-blue);

    &.btn-next span::after {
      background-image: url('~@/assets/img/icons/chevron-forward.svg');
    }
  }

  &.btn-tertiary {
    border: 1px solid var(--secondary-900);
    background: var(--secondary-900);
    color: var(--primary-900);

    &.btn-next span::after {
      background-image: url('~@/assets/img/icons/chevron-forward.svg');
    }
  }

  &.btn-next {
    span {
      position: relative;
      margin-right: 15px;

      &:after {
        right: -24px;
        bottom: -1.5px;
        position: absolute;
        width: 22px;
        height: 22px;
        background-image: url('~@/assets/img/icons/chevron-forward.svg');
        background-repeat: no-repeat;
        content: '';
      }
    }
  }
}
</style>
